import type { NavigationProp, ParamListBase } from "@react-navigation/core";
import { RouteProp, useIsFocused } from "@react-navigation/native";
import React, { FC, useEffect } from "react";
import { ActivityIndicator, StyleSheet, Text, View } from "react-native";
import { useSelector } from "react-redux";

import { tAdminConnection } from "tmt_api/src/types/types";

import { useAppDispatch } from "src/redux";
import { kColors } from "src/kColors";
import { kRoute } from "src/kRoute";
import { setBackButtonRoute } from "src/redux/navigation/thunkActions";
import * as connectionDb from "src/db/connection.db";
import { useIsMobile } from "src/hooks/ui";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { PdStack } from "src/components/layout";
import { acceptApplication, declineApplication } from "src/redux/Application/actions";
import { kStyleConst } from "src/kStyle";

import {
  BrandApplication,
  NumberInput,
  PageContainer,
  RepApplication,
  RetailApplication,
  UserApplicationInfo,
} from "../components";
import { YesNo } from "src/components/YesNo";
import { formatInputPercent } from "src/util/gFormatting";
import { tValidType } from "src/components/FormInputs";

type tpViewConnectionParamList = {
  connection: {
    id: string;
    prev?: typeof kRoute[keyof typeof kRoute];
  };
};
const P06_05_ViewConnectionAdmin: FC<{
  route: RouteProp<tpViewConnectionParamList, "connection">;
  navigation: NavigationProp<ParamListBase>;
}> = ({ route, navigation }) => {
  const { id, prev } = route.params;
  const isMobile = useIsMobile();
  const isFocused = useIsFocused();
  const dispatch = useAppDispatch();
  const [connection, setConnection] = React.useState<tAdminConnection | undefined>(undefined);
  const isBig = useSelector((state) => state.NavigationReducer.drawerType === "permanent");
  const rApplication = useSelector((state) => state.ApplicationReducer);
  const [isExclusive, setExclusive] = React.useState(false);
  const [commissionFee, setCommissionFee] = React.useState(0.15);
  const [serviceFee, setServiceFee] = React.useState(0.05);
  const [isValid, setValid] = React.useState<tValidType>({});
  const [isLoading, setLoading] = React.useState(true);

  const handleOnAccept = () => {
    if (connection) {
      dispatch(acceptApplication(connection.uuid, rApplication, { isExclusive, commissionFee, serviceFee }));
      navigation.goBack();
    }
  };

  const handleOnDecline = () => {
    if (connection) {
      dispatch(declineApplication(connection.uuid));
      navigation.goBack();
    }
  };

  useEffect(() => {
    if (id === undefined) {
      navigation.goBack();
    }
  }, [navigation, id]);

  useEffect(() => {
    (async () => {
      const connectionDocument = await connectionDb.documentAdminConnection(route.params?.id).get();
      setConnection(connectionDocument.data());
    })();
  }, [route.params?.id, setConnection]);

  useEffect(() => {
    if (connection) {
      if (connection.application.type === "brand") {
        dispatch({ type: "UPDATE_BRAND_APPLICATION", applicationPart: connection.application, reset: true });
      } else if (connection.application.type === "rep") {
        dispatch({ type: "UPDATE_REP_APPLICATION", applicationPart: connection.application, reset: true });
      } else if (connection.application.type === "retailer") {
        dispatch({ type: "UPDATE_RETAIL_APPLICATION", applicationPart: connection.application, reset: true });
      }

      setLoading(false);
    }
  }, [connection, dispatch]);

  useEffect(() => {
    dispatch(setBackButtonRoute(prev ? prev : kRoute.P06));
  }, [dispatch, prev]);

  React.useEffect(() => {
    if (!isFocused) {
      dispatch({ type: "APPLICATION_RESET" });
    }
  }, [dispatch, isFocused]);

  React.useEffect(() => {
    if (!isFocused) {
      setConnection(undefined);
    }
  }, [isFocused]);

  if (isLoading) {
    return (
      <View style={[styles.flex1, styles.alignCenter, styles.justifyCenter]}>
        <ActivityIndicator size="large" color={kColors.brandDarkGreen} />
      </View>
    );
  }

  return (
    <PageContainer
      viewStyle={styles.gStackScreenDefault}
      scrollViewContainerStyle={isBig ? styles.gApplyViewAdmin : styles.gApplyViewViewNoMargin}
      scrollViewStyle={styles.gTablePageScrollView}
    >
      <UserApplicationInfo loggedIn={true} />
      {rApplication.type === "brand" && <BrandApplication isValid={isValid} setValid={setValid} />}
      {rApplication.type === "rep" && <RepApplication isValid={isValid} setValid={setValid} />}
      {rApplication.type === "retailer" && <RetailApplication isValid={isValid} setValid={setValid} />}
      {rApplication.type === "brand" && (
        <View style={styles.gTablePageContainerAdmin}>
          <View style={styles.gFormContainer}>
            <Text style={styles.sectionTitle}>Admin Abilities</Text>

            <View style={styles.gLinkBankFormItemYesNo}>
              <Text>Is this company EXCLUSIVE?</Text>
              <YesNo
                myState={isExclusive}
                myText={["No", "Yes"]}
                onPressTrue={() => {
                  setExclusive(true);
                }}
                onPressFalse={() => {
                  setExclusive(false);
                }}
              />
            </View>

            <View style={styles.gFormRowNoMargin}>
              <View style={styles.gLinkBankFormItem}>
                <Text>Commission Fee</Text>
                <NumberInput
                  value={formatInputPercent(commissionFee * 100)}
                  unit={{ label: "%", location: "end" }}
                  maxLength={4}
                  onChangeNumber={(myFee) => {
                    setCommissionFee(myFee / 100);
                  }}
                  style={styles.gLinkBankInput}
                />
              </View>
              <View style={styles.gLinkBankFormItemBottomMargin}>
                <Text>Service Fee</Text>

                <NumberInput
                  value={formatInputPercent(serviceFee * 100)}
                  maxLength={4}
                  onChangeNumber={(myFee) => {
                    setServiceFee(myFee / 100);
                  }}
                  unit={{ label: "%", location: "end" }}
                  style={styles.gLinkBankInput}
                />
              </View>
            </View>
          </View>
        </View>
      )}
      <PdStack isReversed={isMobile} direction={isMobile ? "vertical" : "horizontal"} style={styles.section}>
        <PdButton colorScheme="red" onPress={handleOnDecline} style={styles.mlAuto}>
          <PdButtonLabel>Decline</PdButtonLabel>
        </PdButton>
        <PdButton onPress={handleOnAccept}>
          <PdButtonLabel>Accept</PdButtonLabel>
        </PdButton>
      </PdStack>
    </PageContainer>
  );
};

export const P06_05 = React.memo(P06_05_ViewConnectionAdmin);

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  flex1: {
    flex: 1,
  },
  gApplyViewAdmin: {
    alignItems: "stretch",
    backgroundColor: kColors.brandLight,
    flexDirection: "column",
    flexGrow: 1,
  },
  gApplyViewViewNoMargin: {
    alignItems: "stretch",
    backgroundColor: kColors.brandLight,
    flexDirection: "column",
    flexGrow: 1,
  },
  gFormContainer: {
    marginBottom: 12 * kStyleConst.kSpacing,
    marginHorizontal: 13 * kStyleConst.kSpacing,
    marginTop: 12 * kStyleConst.kSpacing,
    zIndex: 1,
  },
  gFormRowNoMargin: {
    alignItems: "flex-start",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    minWidth: kStyleConst.kApplyMinWidth,
  },
  gLinkBankFormItem: {
    flexGrow: 1,
    marginBottom: 3 * kStyleConst.kSpacing,
    marginRight: 8 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
  },
  gLinkBankFormItemBottomMargin: {
    flexGrow: 1,
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
  },
  gLinkBankFormItemYesNo: {
    flexDirection: "row",
    flexGrow: 1,
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginBottom: 3 * kStyleConst.kSpacing,
    marginRight: 8 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
  },
  gLinkBankInput: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    borderRadius: 2,
    flex: 1,
    height: 36,
    marginLeft: 0,
    marginTop: 5,
    minWidth: kStyleConst.kApplyMinWidth,
    outlineColor: kColors.transparent,
    paddingLeft: kStyleConst.kSpacing,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  gStackScreenDefault: {
    backgroundColor: kColors.brandLight,
    flex: 1,
  },
  gTablePageContainerAdmin: {
    backgroundColor: kColors.white,
    borderRadius: 8,
    flexGrow: 1,
    marginBottom: 7 * kStyleConst.kSpacing,
  },
  gTablePageScrollView: {
    backgroundColor: kColors.brandLight,
    flexGrow: 1,
  },
  justifyCenter: {
    justifyContent: "center",
  },
  mlAuto: {
    marginLeft: "auto",
  },
  section: {
    backgroundColor: kColors.white,
    borderRadius: 8,
    flexGrow: 1,
    marginVertical: 4 * kStyleConst.kSpacing,
    paddingHorizontal: 13 * kStyleConst.kSpacing,
    paddingVertical: 6 * kStyleConst.kSpacing,
    zIndex: 2,
  },
  sectionTitle: {
    alignSelf: "flex-start",
    fontSize: 22,
    marginBottom: 2 * kStyleConst.kSpacing,
  },
});
